import { AfterViewInit, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, firstValueFrom, merge, Observable, of, Subject, forkJoin, EMPTY } from 'rxjs';
import { distinctUntilChanged, filter, finalize, first, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AgreementFormBaseComponent } from 'src/app/base-components/worker-agreement-form-base.component';
import { WorkerService } from 'src/app/data/worker.service';
import { EmployerObjectDictionaryDto } from 'src/app/models/dtos/employer-object-dictionary-dto';
import { WageTypeDto } from 'src/app/models/dtos/wage-type-dto';
import { EmploymentType } from 'src/app/models/enums/employment-type-enum';
import { WorkerAgreement } from 'src/app/models/WorkerAgreement';
import { ModuleName } from 'src/app/subscription-package';
import { DictionaryItem } from '../../models/dictionary-item';
import { TreeViewerComponent, TreeViewerComponentData } from '../../shared/components/tree-viewer/tree-viewer.component';
import { Messages } from 'src/app/common/enums/messages';
import { TreeNode } from '../../shared/models/tree-node';
import { CheckEmploymentHistoryDialogService } from 'src/app/shared/messages/check-employment-history-dialog/service/check-employment-history-dialog.service';
import { CompanyDto } from 'src/app/models/dtos/CompanyDto';
import { Permission } from 'src/app/common/enums';
import { LegalizationDataForWorkerAgreementDto } from 'src/app/models/dtos/worker-agreements/legalization-data-for-worker-agreement-dto';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { EmployerService } from 'src/app/data/employer.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-agreement-form',
  templateUrl: './agreement-form.component.html',
  styleUrls: ['./agreement-form.component.scss'],
  standalone: false,
})
export class AgreementFormComponent extends AgreementFormBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  private workerService = inject(WorkerService);
  protected route = inject(ActivatedRoute);
  private checkEmploymentHistoryDialogService = inject(CheckEmploymentHistoryDialogService);
  private employerService = inject(EmployerService);
  private translate = inject(TranslateService);

  listOfRecruiters$: Observable<DictionaryItem[]> = this.dictionaryService.getRecruiters();
  recruiterModuleEnabled: boolean;
  recruiterIdActive: boolean;

  protected readonly employmentTypeId: EmploymentType;
  public readonly moduleNames = ModuleName;
  public listOfJobTitles$: Observable<DictionaryItem[]> = of([]);
  public organizationalUnitsSubject$ = new BehaviorSubject<number>(null);
  private jobTitleLoadTrigger$ = new BehaviorSubject<number>(null);

  externalWorkerFullNameSubject = new Subject<number>();
  externalWorkerFullName$: Observable<string> = this.externalWorkerFullNameSubject
    .asObservable()
    .pipe(switchMap((id: number) => this.workerService.getWorkerFullName(id)));

  selectedEmployerObject: EmployerObjectDictionaryDto;
  selectedCompany: CompanyDto;

  agreementFormGroup: UntypedFormGroup;

  isWagesCountValid$: Observable<boolean>;
  listOfWageTypes: WageTypeDto[];
  hasAnyDescriptiveWageType$ = this.listOfWageTypes$.pipe(
    tap((wts) => (this.listOfWageTypes = wts)),
    map((wts) => wts.some((wt) => wt.IsDescriptiveType)),
  );
  listOfOrganizationalUnits$: Observable<TreeNode[]> = of([]);
  isForeigner: boolean = false;
  workerAgreementLegalizationData: LegalizationDataForWorkerAgreementDto | null = null;

  get isSubmitForLegalizationEnabled(): boolean {
    const formData = this.agreementFormGroup.getRawValue();
    return !!(formData.company && formData.employer && formData.employerObject);
  }

  constructor() {
    super();

    const matchAll = this.router.url.match(/\d+/g);
    this.employmentTypeId = +matchAll[1];
    this.indefiniteContractPossible = this.employmentTypeId != EmploymentType.TemporaryMandateAgreement;

    this.handleLocationChange();
    this.onSupervisorChange();
  }

  get company() {
    return this.agreementFormGroup.get('company') as UntypedFormControl;
  }

  get employer() {
    return this.agreementFormGroup.get('employer') as UntypedFormControl;
  }

  get location() {
    return this.agreementFormGroup.get('location') as UntypedFormControl;
  }

  get employerObject() {
    return this.agreementFormGroup.get('employerObject') as UntypedFormControl;
  }

  get agreementType() {
    return this.agreementFormGroup.get('agreementType') as UntypedFormControl;
  }

  get conclusionDate() {
    return this.agreementFormGroup.get('conclusionDate') as UntypedFormControl;
  }

  get employmentDateFrom() {
    return this.agreementFormGroup.get('employmentDateFrom') as UntypedFormControl;
  }

  get employmentDateTo() {
    return this.agreementFormGroup.get('employmentDateTo') as UntypedFormControl;
  }

  get wages() {
    return this.agreementFormGroup.get('wages') as UntypedFormArray;
  }

  get agreementTemplate() {
    return this.agreementFormGroup.get('agreementTemplate') as UntypedFormControl;
  }

  get noticePeriod() {
    return this.agreementFormGroup.get('noticePeriod') as UntypedFormControl;
  }

  get responsibilities() {
    return this.agreementFormGroup.get('responsibilities') as UntypedFormControl;
  }

  get paymentDeadline() {
    return this.agreementFormGroup.get('paymentDeadline') as UntypedFormControl;
  }

  get isForIndefinitePeriod() {
    return this.agreementFormGroup.get('isForIndefinitePeriod') as UntypedFormControl;
  }

  get mpk() {
    return this.agreementFormGroup.get('mpk') as UntypedFormControl;
  }

  get workEmail() {
    return this.agreementFormGroup.get('workEmail') as UntypedFormControl;
  }

  get organizationalUnit() {
    return this.agreementFormGroup.get('organizationalUnit') as UntypedFormControl;
  }

  get jobTitle() {
    return this.agreementFormGroup.get('jobTitle') as UntypedFormControl;
  }

  get recruiter() {
    return this.agreementFormGroup.get('recruiter') as UntypedFormControl;
  }

  isValid() {
    return this.agreementFormGroup.invalid;
  }

  async ngOnInit(): Promise<void> {
    this.onOrganizationalUnitChange();
    this.onJobTitleChange();
    this.onCompanyChange();

    this.route.paramMap.pipe(takeUntil(this.unsubscribe$)).subscribe(async (params) => {
      this.workerAgreementId = +params.get('agreementId');

      this.route.paramMap.subscribe((params) => {
        this.workerId = +params.get('workerId');
        if (this.workerId) {
          this.externalWorkerFullNameSubject.next(this.workerId);
        }

        if (this.workerAgreementId) {
          this.fetchWorkerAgreement(this.workerAgreementId);
        } else {
          this.addNewWage();

          this.workerService
            .getWorkerDataForAgreementForm([this.workerId])
            .pipe(first())
            .subscribe({
              next: async (data) => {
                if (data.length > 0) {
                  this.isForeigner = data[0].IsForeigner;
                  this.workEmail.setValue(data[0].WorkEmail);
                }

                await this.setInitialValues(null);
              },
            });
        }
      });
    });

    this.initEventHandlers();
    this.getCurrentSupervisor();

    this.isWagesCountValid$ = combineLatest([this.listOfWageTypes$, this.wages.valueChanges]).pipe(
      map(([wageTypes, wages]) =>
        wageTypes.some(
          (wageType) =>
            (wageType.MinCount && wageType.MinCount > wages.filter((wage) => wage.wageTypeId === wageType.Id).length) ||
            (wageType.MaxCount && wageType.MaxCount < wages.filter((wage) => wage.wageTypeId === wageType.Id).length),
        ),
      ),
    );

    this.handleResponsibilitiesChange();
    this.realizeRecruiterOnEmployerChange();
    this.recruiterModuleEnabled = await this.modulePermissionService.isAllowedModule(this.moduleNames.Recruiters);
  }

  protected onSubmitForLegalization(): void {
    if (!this.isSubmitForLegalizationEnabled) {
      this.markRequiredLegalizationFieldsAsInvalid();
      return;
    }

    this.dialog
      .open(ConfirmDialogComponent, { data: { title: Messages.ConfirmSubmitForLegalizationTitle, message: Messages.ConfirmSubmitForLegalization } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? super.submitForLegalization() : EMPTY)),
      )
      .subscribe();
  }

  private markRequiredLegalizationFieldsAsInvalid(): void {
    const requiredFields = ['company', 'employer', 'employerObject'];

    requiredFields.forEach((fieldName) => {
      const control = this.agreementFormGroup.get(fieldName);
      if (!control.value) {
        control.setErrors({ required: true });
        control.markAsTouched();
      }
    });
  }

  ngAfterViewInit(): void {
    this.handleEmployerSelectionChange();
    this.handleIsForIndefinitePeriodChange();
    this.checkEmploymentHistoryDialogService.checkAndOpenModal(this.employmentTypeId);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  isDescriptiveType(wageTypeId: number) {
    return this.listOfWageTypes.find((wts) => wts.Id === wageTypeId)?.IsDescriptiveType;
  }

  getWageTypeName(wageTypeId: number) {
    return this.listOfWageTypes.find((wts) => wts.Id === wageTypeId)?.Name;
  }

  isLegalizationFieldVisible(): boolean {
    return this.isForeigner && this.selectedCompany?.HasLegalization;
  }

  realizeRecruiterOnEmployerChange() {
    combineLatest([this.listOfEmployers$, this.employer.valueChanges])
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(([employers, employerId]) => {
          const selectedEmployer = employers.find((p) => p.Id == employerId);

          if (!selectedEmployer?.Id) {
            return of({ hasAccess: false, recruiter: null });
          }

          const hasAccess$ = this.recruiterModuleEnabled ? this.employerService.hasAccessToRecruiter(selectedEmployer.Id) : of(false);
          const recruiter$ = !this.workerAgreementId ? this.workerService.getNewestRecruiter(this.workerId, selectedEmployer.Id) : of(null);

          return combineLatest([hasAccess$, recruiter$]).pipe(
            map(([hasAccess, recruiter]) => ({ hasAccess, recruiter })));
        }),
      )
      .subscribe(({ hasAccess, recruiter }) => {
        this.updateRecruiterFieldBasedOnAccess(hasAccess);

        if (recruiter?.Id) {
          this.recruiter.setValue(recruiter.Id);
          this.recruiter.updateValueAndValidity();
        }
      });
  }

  private updateRecruiterFieldBasedOnAccess(hasAccess: boolean): void {
    this.recruiterIdActive = hasAccess;

    if (!hasAccess) {
      this.recruiter.setValue(null);
      this.recruiter.clearValidators();
    } else {
      this.recruiter.setValidators(Validators.required);
    }

    this.recruiter.updateValueAndValidity();
  }

  private fetchWorkerAgreement(agreementId: number): void {
    if (!agreementId) {
      return;
    }

    const workerAgreement$ = this.workerAgreementService.getWorkerAgreement(agreementId);
    const legalizationData$ = this.isPermittedToLegalization()
      ? this.workerAgreementService.getLegalizationDataForWorkerAgreements([agreementId])
      : of(null);

    forkJoin({
      workerAgreement: workerAgreement$,
      legalizationData: legalizationData$,
    })
      .pipe(first())
      .subscribe(async (res) => {
        this.setWorkerAgreement(res.workerAgreement);
        this.isForeigner = this.workerAgreement.IsForeigner;
        await this.setAdditionalValues(res.workerAgreement);
        await this.setInitialValues(this.workerAgreement);

        if (this.wages.length < this.miminumWages) {
          this.addNewWage();
        }

        if (res.legalizationData && res.legalizationData.length > 0) {
          this.workerAgreementLegalizationData = res.legalizationData[0];
        }
      });
  }

  private isPermittedToLegalization(): boolean {
    return (
      this.authService.hasPermission(Permission.LegalizationInformationVisibilityToAll) ||
      this.authService.hasPermission(Permission.LegalizationInformationVisibilityToEmployerObjects)
    );
  }

  isDeleteButtonVisible(): boolean {
    return !!this.workerAgreement?.AuthServerUserId && (
      this.authService.hasPermission(Permission.ManageMyExternalWorkersList) ||
      this.authService.hasPermission(Permission.ManageAllExternalWorkersList)
    ) && this.workerAgreement.AuthServerUserId !== this.authService.getAuthServerUserId();
  }

  isDeleteButtonEnabled(): boolean {
    return this.isDeleteButtonVisible() && this.workerAgreement?.WorkerAgreementCountInCompany > 1;
  }

  deleteButtonTooltip(): string {
    if (this.isDeleteButtonVisible() && this.workerAgreement?.WorkerAgreementCountInCompany <= 1) {
      return this.translate.instant('WAD-DeleteAgreementTooltip');
    }
    return '';
  }

  onDeleteAgreementClick(): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: Messages.ConfirmDeletingAgreementTitle,
          message: this.translate.instant('ConfirmDeletingAgreementMessage', { status: this.workerAgreement?.AgreementStatus }),
        },
      })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => {
          if (!isConfirmed) { return EMPTY; }
          this.spinner.show();
          return this.workerAgreementService.deleteAgreement(this.workerAgreementId).pipe(
            finalize(() => this.spinner.hide()),
            tap(() => {
              this.snackbarService.openSuccessSnackBar(Messages.SuccessfullyDeletedAgreement);
              this.router.navigate(['/profile', this.workerId, 'workerAgreements']);
            }),
          );
        }),
      )
      .subscribe();
  }

  private async setAdditionalValues(agreement: WorkerAgreement) {
    const companies = await firstValueFrom(this.listOfCompanies$);
    this.selectedCompany = companies.find((c) => c.Id === this.company.value) || null;

    if (agreement.OrganizationalUnitId) {
      if (this.company && this.company.value) {
        this.organizationalUnitsSubject$.next(this.company.value);
      }

      this.organizationalUnit.setValue({ Id: agreement.OrganizationalUnitId, Name: agreement.OrganizationalUnitName } as DictionaryItem);
      this.jobTitleLoadTrigger$.next(agreement.OrganizationalUnitId);
    } else {
      this.organizationalUnit.setValue(null);
      this.jobTitle.setValue(null);
    }
  }

  public async onOrganizationalUnitClick(): Promise<void> {
    if (!this.company.value) {
      return;
    }

    const res = await firstValueFrom(this.dialog.open(TreeViewerComponent, this.createDialogConfig()).afterClosed().pipe(first()));

    if (res) {
      this.organizationalUnit.setValue(<DictionaryItem>{ Id: res.id, Name: res.name });
      this.jobTitle.setValue(null);
      this.dialog.closeAll();
    }
  }

  public onCompanyChange(): void {
    combineLatest([this.company.valueChanges.pipe(startWith(this.company.value)), this.listOfCompanies$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([companyId, companies]) => {
        if (companyId && companies) {
          this.selectedCompany = companies.find((c) => c.Id === companyId) || null;
          if (this.selectedCompany) {
            this.organizationalUnitsSubject$.next(companyId);
            this.organizationalUnit.setValue(null);
            this.jobTitle.setValue(null);
          }
        }
      });
  }

  protected createDialogConfig(): MatDialogConfig<TreeViewerComponentData> {
    return {
      disableClose: false,
      autoFocus: false,
      panelClass: 'standard-dialog',
      data: {
        header: Messages.ChooseOrganizationalUnit,
        emptyTreeMessage: Messages.EmptyOrganizationalUnitTree,
        datasource$: this.listOfOrganizationalUnits$,
      },
    };
  }

  protected onOrganizationalUnitChange() {
    this.listOfOrganizationalUnits$ = this.organizationalUnitsSubject$.asObservable().pipe(
      switchMap((organizationalUnitId: number) => {
        if (organizationalUnitId === null) {
          return [];
        }
        return this.dictionaryService
          .getOrganizationalUnits(organizationalUnitId, this.employmentTypeId)
          .pipe(map((arr) => arr.map((ou) => <TreeNode>{ ...ou, Checked: false, Expanded: false })));
      }),
      takeUntil(this.unsubscribe$),
    );
  }

  protected onJobTitleChange(): void {
    this.listOfJobTitles$ = merge(
      this.organizationalUnit.valueChanges.pipe(
        distinctUntilChanged(),
        map((value) => value?.Id || null),
      ),
      this.jobTitleLoadTrigger$.asObservable(),
    ).pipe(
      filter((UnitId) => UnitId !== null),
      switchMap((UnitId: number) => {
        return this.dictionaryService.getJobTitles(null, UnitId).pipe(
          tap((jobTitles) => {
            if (this.workerAgreement && this.workerAgreement.JobTitleId && UnitId === this.workerAgreement.OrganizationalUnitId) {
              this.jobTitle.setValue(this.workerAgreement.JobTitleId);
            }
          }),
        );
      }),
      takeUntil(this.unsubscribe$),
    );
  }
}
